<template>
  <section
    v-if="visible"
    class="needs-attention-card"
    data-testid="needs-attention-card"
    aria-label="Needs attention"
  >
    <header class="needs-attention-card__header">
      <h2 class="needs-attention-card__title">Needs attention</h2>
      <span class="needs-attention-card__summary">{{ summary }}</span>
    </header>

    <AttentionRow
      v-for="row in rows"
      :key="row.id"
      :count="row.count"
      :title="row.title"
      :detail="row.detail"
      :action-label="row.actionLabel"
      @action="onRowAction(row)"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import AttentionRow from "@components/AttentionRow/AttentionRow.vue";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { LD_FLAG_IDEA_MDP_622 } from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";

/**
 * Organization > Dashboard "Needs attention" card (MDP-622 / ER-12269).
 *
 * Lists gaps between objects, work the tiles cannot show. Version 1 has one row:
 * Employees not occupying a primary position. Rows are data-driven from ROW_DEFINITIONS so later
 * rows (Positions with no Role, Roles with no Responsibilities, Invalid Positions)
 * can be added without duplicating markup. A row is shown only when its count is
 * greater than 0; when no row applies the whole card is not rendered (Rule 12).
 */
const ROW_DEFINITIONS = Object.freeze([
  {
    id: "employees-without-position",
    title: "Employees not occupying a primary position",
    detail: "They cannot be assigned work or readiness",
    actionLabel: "Assign",
    tab: "employees",
    // The Employees list already ships an "Occupies" filter (filter_by_occupies ->
    // primary_positions__status=active). does_not_occupy is exactly the inverse of the
    // ER-12267 count (active employees with no current primary Position), so the card
    // count and the filtered list it opens always show the same employees.
    filter: ["active", "does_not_occupy"],
  },
]);

const emit = defineEmits(["action"]);

// `employeesWithoutPosition` lets consumers (and Storybook) drive the single v1 row
// directly, while the store remains the source of truth for the real dashboard. The
// prop defaults to null so the store getter is used when the prop is not provided.
const props = defineProps({
  employeesWithoutPosition: {
    type: Number,
    default: null,
  },
});

const sidebarCountStore = useSidebarCountStore();

// ER-12269 / idea-MDP-622 — the card is new behaviour gated behind the MDP-622 flag.
// Default true avoids a flash in the normal, flag-unset case (same pattern as the
// Departments tile); the count itself also comes from the flag-gated v2 field.
const flagEnabled = ref(true);
onMounted(async () => {
  try {
    flagEnabled.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_622, true)) !== false;
  } catch (error) {
    console.error("Failed to resolve Needs attention card flag:", error);
    flagEnabled.value = true;
  }
});

const countByRowId = {
  "employees-without-position": () =>
    props.employeesWithoutPosition !== null
      ? props.employeesWithoutPosition
      : sidebarCountStore.getEmployeesWithoutPositionCount,
};

const rows = computed(() =>
  ROW_DEFINITIONS.map((meta) => ({
    ...meta,
    count: countByRowId[meta.id] ? countByRowId[meta.id]() : 0,
  })).filter((row) => row.count > 0)
);

const totalItems = computed(() => rows.value.reduce((sum, row) => sum + row.count, 0));

const summary = computed(() =>
  totalItems.value === 1 ? "1 item to review" : `${totalItems.value} items to review`
);

const visible = computed(() => flagEnabled.value && rows.value.length > 0);

function onRowAction(row) {
  emit("action", { id: row.id, tab: row.tab, filter: [...row.filter] });
}
</script>

<style scoped>
.needs-attention-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
}

.needs-attention-card__header {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.needs-attention-card__title {
  margin: 0;
  color: #101828;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.needs-attention-card__summary {
  color: #475467;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}
</style>
